import { Fragment, useEffect, useState } from 'react';
import { api, type OnboardResult, type ShipTo, type VendorRow } from '../api';
import { LoginAsCamp, useDebounced, useLoad } from '../lib';
import { AddressFields, BLANK_ADDRESS, ShipToEditor, missingFields, shipToOneLine, toShipToInput, type AddressDraft } from '../shipto';

// Provision a new camp end-to-end (org + delivery address + vendor assignments + starter guide + invite
// code, registered on the platform org registry), then track every camp that came through this flow —
// where it ships, its codes — and mint more codes. The delivery address is required: commerce's
// checkout refuses a camp without one, and a new camp has no order history to supply one.
export function OnboardingPanel({ canEditShipTo }: { canEditShipTo: boolean }) {
  const [name, setName] = useState('');
  const [guideName, setGuideName] = useState('');
  const [basis, setBasis] = useState<'house' | 'consenting'>('house');
  const [vendorSearch, setVendorSearch] = useState('');
  // useLoad drops a response that lands after a newer search, and keeps null while one is in flight,
  // so an empty list means "none match" only once the answer is in.
  const dVendorSearch = useDebounced(vendorSearch);
  const { data: vendors, err: vendorsErr } = useLoad(() => api.commerceVendors(dVendorSearch), [dVendorSearch]);
  const [picked, setPicked] = useState<Map<number, string>>(new Map());
  const [address, setAddress] = useState<AddressDraft>(BLANK_ADDRESS);
  const [editingShipTo, setEditingShipTo] = useState<number | null>(null);
  // Addresses saved from this list since it loaded, so a save updates its row without a reload that
  // would unmount the open editor (and its confirmation). A fresh load supersedes them.
  const [savedShipTo, setSavedShipTo] = useState<Map<number, ShipTo | null>>(new Map());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [result, setResult] = useState<OnboardResult | null>(null);
  const [minting, setMinting] = useState<number | null>(null);
  const { data: orgs, err, reload } = useLoad(() => api.commerceOnboardedOrgs());
  useEffect(() => { if (orgs) setSavedShipTo(new Map()); }, [orgs]);

  const toggleVendor = (v: VendorRow) => setPicked((prev) => {
    const next = new Map(prev);
    if (next.has(v.id)) next.delete(v.id); else next.set(v.id, v.name);
    return next;
  });

  async function submit() {
    setMsg(null);
    setResult(null);
    setBusy(true);
    try {
      const r = await api.commerceCreateOrg({
        name: name.trim(),
        vendorIds: [...picked.keys()],
        pricingBasis: basis,
        ...(guideName.trim() ? { starterGuideName: guideName.trim() } : {}),
        shipTo: toShipToInput(address),
      });
      setResult(r);
      setName(''); setGuideName(''); setPicked(new Map()); setVendorSearch(''); setAddress(BLANK_ADDRESS);
      reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function mintMore(orgId: number) {
    setMinting(orgId);
    try { await api.commerceMintInvite(orgId, { role: 'buyer' }); reload(); }
    catch { /* row-level feedback via list refresh */ }
    finally { setMinting(null); }
  }

  const addressMissing = missingFields(address);
  const canSubmit = name.trim().length > 0 && picked.size > 0 && addressMissing.length === 0 && !busy;

  return (
    <div className="stack">
      <h1>Onboarding</h1>
      <p className="sub">Provision a new camp: org, delivery address, vendor assignments, a starter order guide, and an invite code buyers redeem to join. The org is registered on the Tavezio platform (or marked pending until the registry is reachable).</p>
      <section className="card">
        <div className="form-row">
          <label className="field"><span>Camp name</span>
            <input className="search" placeholder="Camp Wildwood" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field"><span>Starter guide name (optional)</span>
            <input className="search" placeholder="Starter Guide" value={guideName} onChange={(e) => setGuideName(e.target.value)} />
          </label>
          <label className="field"><span>Pricing basis</span>
            <span className="radio-group">
              <label><input type="radio" name="basis" checked={basis === 'house'} onChange={() => setBasis('house')} /> House</label>
              <label><input type="radio" name="basis" checked={basis === 'consenting'} onChange={() => setBasis('consenting')} /> Consenting</label>
            </span>
          </label>
          <button className="btn-sm" disabled={!canSubmit} onClick={submit}>{busy ? 'Provisioning…' : 'Create camp'}</button>
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <span>Vendors ({picked.size} selected — at least one required)</span>
          {picked.size > 0 && (
            <div className="picked-row">
              {[...picked.entries()].map(([id, vname]) => (
                <button key={id} className="pill pick" title="remove" onClick={() => setPicked((p) => { const n = new Map(p); n.delete(id); return n; })}>{vname} ✕</button>
              ))}
            </div>
          )}
          <input className="search" placeholder="search vendors…" value={vendorSearch} onChange={(e) => setVendorSearch(e.target.value)} />
          <div className="pick-list">
            {(vendors ?? []).slice(0, 12).map((v) => (
              <button key={v.id} className={`ta-opt${picked.has(v.id) ? ' picked' : ''}`} onClick={() => toggleVendor(v)}>
                {picked.has(v.id) ? '✓ ' : ''}{v.name} <span className="mono" style={{ opacity: 0.55 }}>#{v.id}</span>
              </button>
            ))}
            {vendorsErr && <div className="msg bad" style={{ padding: 8 }}>⚠ {vendorsErr}</div>}
            {!vendors && !vendorsErr && <div className="loading" style={{ padding: 8 }}>Loading vendors…</div>}
            {vendors && vendors.length === 0 && <div className="loading" style={{ padding: 8 }}>No vendors match.</div>}
          </div>
        </div>
        <div className="field" style={{ marginTop: 12 }}>
          <span>Delivery address — required: every purchase order ships here, and a camp without one cannot check out</span>
          <AddressFields value={address} onChange={setAddress} campName={name.trim()} />
          {name.trim() && picked.size > 0 && addressMissing.length > 0 && <p className="muted" style={{ margin: 0 }}>Needs {addressMissing.join(', ')}.</p>}
        </div>
        {msg && <p className="msg bad" style={{ marginTop: 12 }}>⚠ {msg.replace(/^⚠ /, '')}</p>}
        {result && (
          <div className="onboard-result">
            <p className="msg good">✓ {result.org.name} provisioned — org #{result.org.id}, {result.vendors} vendor{result.vendors === 1 ? '' : 's'}, guide “{result.guide.name}” <LoginAsCamp orgId={result.org.id} name={result.org.name} /></p>
            {result.shipTo && <p>Ships to {shipToOneLine(result.shipTo)}</p>}
            <p>Invite code <span className="invite-chip">{result.invite.code}</span> ({result.invite.role}, {result.invite.max_uses} uses{result.invite.expires_at ? `, expires ${result.invite.expires_at.slice(0, 10)}` : ''})</p>
            <p className={result.org.platform_org_id ? 'muted' : 'msg bad'}>
              {result.org.platform_org_id ? `Platform: registered (${result.org.platform_org_id})` : `Platform: pending${result.warning ? ` — ${result.warning}` : ''}`}
            </p>
          </div>
        )}
      </section>
      {err && <p className="msg bad">⚠ {err}</p>}
      {/* Nine columns: scroll sideways on a narrow window rather than clip the actions. */}
      <section className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>Org</th><th>Camp</th><th>Status</th><th>Platform</th><th>Ships to</th><th className="num">Vendors</th><th className="num">Members</th><th>Invite codes</th><th></th></tr></thead>
          <tbody>
            {(orgs ?? []).map((o) => {
              const shipTo = savedShipTo.has(o.id) ? (savedShipTo.get(o.id) ?? null) : o.ship_to;
              return (
                <Fragment key={o.id}>
                  <tr>
                    <td className="mono">{o.id}</td>
                    <td>{o.name}</td>
                    <td>{o.onboard_status === 'active' ? <span className="pill role-ops">active</span> : <span className="pill">{o.onboard_status ?? '—'}</span>}</td>
                    <td className="mono">{o.platform_org_id ? `${o.platform_org_id.slice(0, 8)}…` : '—'}</td>
                    <td className="shipto-cell">
                      {shipTo ? shipToOneLine(shipTo) : <span className="pill shipto-src none">none — cannot check out</span>}
                      <button className="link edit" style={{ marginLeft: 8 }} onClick={() => setEditingShipTo(editingShipTo === o.id ? null : o.id)}>
                        {editingShipTo === o.id ? 'CLOSE' : canEditShipTo ? 'EDIT' : 'VIEW'}
                      </button>
                    </td>
                    <td className="num">{o.vendors}</td>
                    <td className="num">{o.members}</td>
                    <td>
                      {o.invites.map((i) => (
                        <span key={i.code} className="invite-chip" title={`${i.role} · ${i.used}/${i.max_uses} used${i.expires_at ? ` · expires ${i.expires_at.slice(0, 10)}` : ''}`}>{i.code} <em>{i.used}/{i.max_uses}</em></span>
                      ))}
                      {o.invites.length === 0 && '—'}
                    </td>
                    <td className="actions">
                      <button className="btn-sm ghost" disabled={minting === o.id} onClick={() => mintMore(o.id)}>{minting === o.id ? 'Minting…' : 'Mint code'}</button>
                      <LoginAsCamp orgId={o.id} name={o.name} />
                    </td>
                  </tr>
                  {editingShipTo === o.id && (
                    <tr><td colSpan={9}>
                      <ShipToEditor orgId={o.id} name={o.name} canEdit={canEditShipTo}
                        onSaved={(a) => setSavedShipTo((m) => new Map(m).set(o.id, a.shipTo))} />
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
            {orgs && orgs.length === 0 && <tr><td colSpan={9} className="loading">No camps onboarded through this flow yet.</td></tr>}
          </tbody>
        </table>
        {!orgs && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
